import { useLayoutEffect, useRef, useState } from 'react';
import { cn } from '~/lib/cn';

type Country = {
  name: string;
  currencies: string;
  /** Circular flag SVG under /public/images/flags (from the circle-flags set). */
  flag: string;
};

/**
 * Country / currency list shown in the Transfer card (Figma reference).
 * Order: (1) featured top entries, (2) other local-currency payouts.
 * Europe/SEPA stays a single row.
 */
const countries: Country[] = [
  // 1 — Featured (keep currencies as-is)
  { name: 'Egypt', currencies: 'EGP, USD, EUR, GBP', flag: '/images/flags/eg.svg' },
  { name: 'Pakistan', currencies: 'PKR, USD, EUR, GBP', flag: '/images/flags/pk.svg' },
  { name: 'Bangladesh', currencies: 'BDT, USD, EUR, GBP', flag: '/images/flags/bd.svg' },
  { name: 'United States', currencies: 'USD', flag: '/images/flags/us.svg' },
  { name: 'Europe', currencies: 'USD, EUR', flag: '/images/flags/eu.svg' },

  // 2 — Remaining local-currency payouts
  { name: 'Turkey', currencies: 'TRY, USD', flag: '/images/flags/tr.svg' },
  { name: 'Morocco', currencies: 'MAD, USD', flag: '/images/flags/ma.svg' },
  { name: 'United Arab Emirates', currencies: 'AED, USD', flag: '/images/flags/ae.svg' },
  { name: 'Qatar', currencies: 'QAR, USD', flag: '/images/flags/qa.svg' },
  { name: 'Saudi Arabia', currencies: 'SAR, USD', flag: '/images/flags/sa.svg' },
  { name: 'Nigeria', currencies: 'NGN, USD', flag: '/images/flags/ng.svg' },
  { name: 'Syria', currencies: 'SYP, USD', flag: '/images/flags/sy.svg' },
  { name: 'United Kingdom', currencies: 'GBP, USD', flag: '/images/flags/gb.svg' },
];

/**
 * Floating country / currency selector, overlaid on the Transfer feature image.
 *
 * White rounded card with a vertical list of countries (circular flag + name +
 * currency list). A single fully-rounded grey highlight slides/resizes to track
 * the hovered row (same technique as the hero balances card), resting on the
 * currently selected row (Egypt by default) when the cursor leaves. Clicking a
 * row selects it. The list is clipped by the card's bottom edge so the last
 * visible row is partially cut off, implying more items.
 */
export function CountrySelectorCard({ className }: { className?: string }) {
  const [selected, setSelected] = useState(0);
  const [hovered, setHovered] = useState<number | null>(null);
  const [reduced, setReduced] = useState(false);
  const [pill, setPill] = useState<{ top: number; height: number } | null>(null);

  const rowRefs = useRef<(HTMLLIElement | null)[]>([]);
  const active = hovered ?? selected;

  useLayoutEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  // Measure the active row (offsets are relative to the positioned <ul>) and
  // move the single highlight to sit behind it.
  useLayoutEffect(() => {
    const el = rowRefs.current[active];
    if (!el) return;
    setPill({ top: el.offsetTop, height: el.offsetHeight });
  }, [active]);

  return (
    <div
      className={cn(
        // Height fits p-5 (40px) + scroll viewport for 3.5 rows (see .country-selector-list).
        'pointer-events-auto flex w-[314px] max-w-[82%] flex-col overflow-hidden rounded-[24px] bg-white p-5 shadow-pill',
        // On mobile the square image is small: use a shorter layout box so the overlay grid can
        // truly centre it, plus a proportional scale-down for clear top/bottom breathing space.
        'origin-center scale-[0.82] lg:scale-100',
        className,
      )}>
      <ul
        className="country-selector-list relative flex flex-col gap-5 overflow-y-auto overscroll-y-contain"
        onMouseLeave={() => setHovered(null)}>
        {/* Single fully-rounded highlight that glides/resizes to the active row. */}
        {pill && (
          <div
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute inset-x-0 top-0 rounded-full bg-black-6',
              !reduced && 'transition-[transform,height] duration-200 ease-nav',
            )}
            style={{ transform: `translateY(${pill.top}px)`, height: pill.height }}
          />
        )}

        {countries.map((country, i) => (
          <li
            key={country.name}
            ref={(el) => {
              rowRefs.current[i] = el;
            }}
            className="relative z-10">
            <button
              type="button"
              aria-pressed={selected === i}
              onClick={() => setSelected(i)}
              onMouseEnter={() => setHovered(i)}
              onFocus={() => setHovered(i)}
              onBlur={() => setHovered(null)}
              className="flex w-full items-center gap-4 rounded-full px-4 py-[10px] text-start">
              <img
                src={country.flag}
                alt=""
                aria-hidden="true"
                loading="lazy"
                decoding="async"
                className="size-8 shrink-0 rounded-full object-cover"
              />
              <span className="flex min-w-0 flex-col gap-[10px]">
                <span className="truncate text-body font-semibold text-ink [text-box-trim:none]">
                  {country.name}
                </span>
                <span className="truncate text-body font-medium text-grey-900 [text-box-trim:none]">
                  {country.currencies}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
